import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FiChevronDown} from 'react-icons/fi';
import {useDebouncedCallback} from 'use-debounce';
import {useActionItems} from '@hooks/features-action-items';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {ItemLoader} from '@ui/organisms/item-loader';
import {MyGoalCard} from '../my-objective/my-goals-v2';
import {
  activeFilterCount,
  filterMyGoals,
  GoalEngagementSummary,
  goalIsOverdueCarryover,
  goalMatchesSearch,
  goalStatusSuggestion,
  sortMyGoals,
  summarizeGoalEngagement,
  summarizeMyGoals,
} from '../my-objective/my-goals-v2.utils';
import {
  emptyMyGoalsFilters,
  MyGoalsFilters,
  MyGoalsLifecycleFilter,
  MyGoalsSort,
} from '../my-objective/my-goals-v2.types';
import {useDirectReportHook} from '../direct-reports/direct-report-hook';
import {TeamGoalsOverview} from '../direct-reports/team-goals-overview';
import {TeamGoalsToolbar} from '../direct-reports/team-goals-toolbar';
import {teamMemberName} from '../direct-reports/team-goals.utils';
import {EmptyPanel, LoadingPanel} from '../direct-reports/styles';
import {
  AllGoalsContent,
  FlatGoalCards,
  GoalSection,
  GoalSectionAvatar,
  GoalSectionCards,
  GoalSectionChevron,
  GoalSectionHeader,
  GoalSectionIdentity,
  GoalSections,
  PaginationBar,
} from './all-goals.styles';
import {AllGoalsGroupBy} from './all-goals.types';
import {
  allGoalGroup,
  allGoalOwner,
  allGoalTeam,
  buildAllGoalSections,
  flattenAllGoals,
  goalEntityId,
} from './all-goals.utils';

const PAGE_SIZE = 10;

const sectionInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

export const AllGoalsPage = observer(() => {
  const {
    loading,
    error,
    goalMembers,
    dateRange,
    setDateRange,
    refetch,
  } = useDirectReportHook('all');
  const {
    authStore: {auth},
    groupStore: {groups},
    groupTypeStore: {groupType},
    usersStore: {users},
  } = useStoreContext();
  const firebaseGoals = useFirebaseFetch('goals');
  const goalMentions = useFirebaseFetch(`users/${auth.user.id}/mentions/goals`);
  const goalActionItems = useActionItems({
    source: 'goal',
    itemId: '',
    userId: '',
    fetchAll: true,
  });

  const [filters, setFilters] = useState<MyGoalsFilters>(emptyMyGoalsFilters);
  const [lifecycle, setLifecycle] = useState<MyGoalsLifecycleFilter>('all');
  const [sort, setSort] = useState<MyGoalsSort>('highest_progress');
  const [groupBy, setGroupBy] = useState<AllGoalsGroupBy>('group');
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [reviewSuggestedOnly, setReviewSuggestedOnly] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [defaultPeriod] = useState(dateRange);

  const debouncedSearch = useDebouncedCallback(
    (value: string) => setSearch(value),
    200,
  );
  const goals = useMemo(() => flattenAllGoals(goalMembers), [goalMembers]);
  const periodGoals = useMemo(
    () =>
      filterMyGoals({
        goals,
        period: dateRange,
        filters: emptyMyGoalsFilters,
        lifecycle: 'all',
      }),
    [dateRange, goals],
  );
  const displayGoals = useMemo(
    () =>
      filterMyGoals({
        goals,
        period: dateRange,
        filters: emptyMyGoalsFilters,
        lifecycle: 'all',
        includeOverdueCarryover: true,
      }),
    [dateRange, goals],
  );
  const carryoverCount = useMemo(
    () =>
      displayGoals.filter((goal) => goalIsOverdueCarryover(goal, dateRange))
        .length,
    [dateRange, displayGoals],
  );
  const summary = useMemo(() => summarizeMyGoals(periodGoals), [periodGoals]);
  const suggestions = useMemo(
    () =>
      periodGoals.reduce((result, goal) => {
        if (goalStatusSuggestion(goal)) result[goal.id] = true;
        return result;
      }, {} as Record<string, boolean>),
    [periodGoals],
  );
  const statusReviewCount = Object.keys(suggestions).length;
  const groupTypeLabel = useMemo(() => {
    const namedTypes = groupType.filter((type: any) => type?.name);
    const customType = namedTypes.find(
      (type: any) =>
        type.isDefault !== true && String(type.isDefault) !== 'true',
    );
    return customType?.name || namedTypes[0]?.name || 'Groups';
  }, [groupType]);
  const emptyGroupLabel = `No ${groupTypeLabel
    .replace(/s$/i, '')
    .toLowerCase()}`;

  const visibleGoals = useMemo(() => {
    const matching = filterMyGoals({
      goals,
      period: dateRange,
      filters,
      lifecycle,
      includeOverdueCarryover: true,
    }).filter((goal) => {
      if (!goalMatchesSearch(goal, search)) return false;
      const ownerId = goalEntityId(goal?.assignee);
      const groupId = allGoalGroup(goal, users, groups, emptyGroupLabel).id;
      const teamId = allGoalTeam(goal, users).id;
      if (selectedOwners.length && !selectedOwners.includes(ownerId)) {
        return false;
      }
      if (selectedGroups.length && !selectedGroups.includes(groupId)) {
        return false;
      }
      if (selectedTeams.length && !selectedTeams.includes(teamId)) return false;
      return !reviewSuggestedOnly || suggestions[goal.id];
    });
    return sortMyGoals(matching, sort);
  }, [
    dateRange,
    emptyGroupLabel,
    filters,
    goals,
    groups,
    lifecycle,
    reviewSuggestedOnly,
    selectedGroups,
    selectedOwners,
    selectedTeams,
    sort,
    search,
    suggestions,
    users,
  ]);
  const sections = useMemo(() => {
    if (groupBy === 'none') return [];
    return buildAllGoalSections(
      visibleGoals,
      users,
      groups,
      groupBy,
      emptyGroupLabel,
    );
  }, [emptyGroupLabel, groupBy, groups, users, visibleGoals]);
  const groupSections = useMemo(
    () =>
      buildAllGoalSections(
        displayGoals,
        users,
        groups,
        'group',
        emptyGroupLabel,
      ),
    [displayGoals, emptyGroupLabel, groups, users],
  );
  const teamSections = useMemo(
    () => buildAllGoalSections(displayGoals, users, groups, 'team'),
    [displayGoals, groups, users],
  );
  const groupOptions = useMemo(
    () =>
      groupSections
        .filter((section) => section.kind === 'group')
        .map((section) => ({id: section.id, name: section.name})),
    [groupSections],
  );
  const teamOptions = useMemo(
    () =>
      teamSections
        .filter((section) => section.kind === 'team')
        .map((section) => ({id: section.id, name: section.name})),
    [teamSections],
  );
  const ownerOptions = useMemo(
    () =>
      Array.from(
        new Map(
          displayGoals
            .map((goal) => allGoalOwner(goal, users))
            .filter(Boolean)
            .map((owner) => [
              goalEntityId(owner),
              {id: goalEntityId(owner), name: teamMemberName(owner)},
            ]),
        ).values(),
      ).sort((first, second) => first.name.localeCompare(second.name)),
    [displayGoals, users],
  );
  const engagementByGoal = useMemo(
    () =>
      displayGoals.reduce((result, goal) => {
        result[goal.id] = summarizeGoalEngagement({
          goal,
          firebaseGoal: firebaseGoals.data?.[goal.id],
          mentions: goalMentions.data?.[goal.id],
          actionItems: goalActionItems.tasks,
          commentsLoading: firebaseGoals.isLoading,
          actionItemsLoading: goalActionItems.isLoading,
        });
        return result;
      }, {} as Record<string, GoalEngagementSummary>),
    [
      firebaseGoals.data,
      firebaseGoals.isLoading,
      goalActionItems.isLoading,
      goalActionItems.tasks,
      goalMentions.data,
      displayGoals,
    ],
  );

  const sectionSignature = sections.map((section) => section.id).join('|');
  useEffect(() => {
    setExpandedSections((current) => {
      const valid = new Set(sections.map((section) => section.id));
      const retained = new Set(
        Array.from(current).filter((sectionId) => valid.has(sectionId)),
      );
      if (retained.size) return retained;
      const company = sections.find((section) => section.kind === 'company');
      const firstSection = sections.find(
        (section) => section.kind === 'group' || section.kind === 'team',
      );
      const defaults = [company?.id, firstSection?.id].filter(
        Boolean,
      ) as string[];
      return new Set(
        defaults.length ? defaults : sections[0] ? [sections[0].id] : [],
      );
    });
  }, [sectionSignature, sections]);

  useEffect(() => {
    setPage(1);
  }, [
    filters,
    groupBy,
    lifecycle,
    reviewSuggestedOnly,
    search,
    selectedGroups,
    selectedOwners,
    selectedTeams,
    sort,
  ]);

  useEffect(() => {
    if (reviewSuggestedOnly && statusReviewCount === 0) {
      setReviewSuggestedOnly(false);
    }
  }, [reviewSuggestedOnly, statusReviewCount]);

  const toggleGoal = useCallback((goalId: string) => {
    setExpandedGoals((current) => {
      const next = new Set(current);
      next.has(goalId) ? next.delete(goalId) : next.add(goalId);
      return next;
    });
  }, []);

  const canManage = (goal: any) => {
    const owner = allGoalOwner(goal, users);
    const ownerId = goalEntityId(owner);
    const reviewerId = goalEntityId(owner?.reviewer);
    return ownerId === auth.user.id || reviewerId === auth.user.id;
  };

  const renderCard = (goal: any) => {
    const owner = allGoalOwner(goal, users);
    const isManagerEditing =
      goalEntityId(owner) !== auth.user.id &&
      goalEntityId(owner?.reviewer) === auth.user.id;
    return (
      <MyGoalCard
        key={goal.id}
        goal={goal}
        availableUsers={users}
        engagement={engagementByGoal[goal.id]}
        expanded={expandedGoals.has(goal.id)}
        onToggle={() => toggleGoal(goal.id)}
        managerEdit={isManagerEditing}
        canManage={canManage(goal)}
        showCreatedAt={!goal?.isKpi}
        footerPrompt="Want to comment or create action items?"
      />
    );
  };

  const totalPages = Math.max(1, Math.ceil(visibleGoals.length / PAGE_SIZE));
  const pageGoals = visibleGoals.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );
  const hasActiveFilters =
    activeFilterCount(filters) > 0 ||
    selectedOwners.length > 0 ||
    selectedGroups.length > 0 ||
    selectedTeams.length > 0 ||
    lifecycle !== 'all' ||
    reviewSuggestedOnly ||
    Boolean(search);

  if (loading) {
    return (
      <LoadingPanel aria-label="Loading All Goals">
        <ItemLoader />
      </LoadingPanel>
    );
  }

  if (error) {
    return (
      <EmptyPanel role="alert">
        <div>
          <h2>All Goals could not be loaded</h2>
          <p>Check your connection and try again.</p>
          <button type="button" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      </EmptyPanel>
    );
  }

  if (goals.length === 0 && !search) {
    return (
      <EmptyPanel>
        <div>
          <h2>No goals have been created</h2>
          <p>Workspace goals will appear here when they are created.</p>
        </div>
      </EmptyPanel>
    );
  }

  return (
    <AllGoalsContent>
      <TeamGoalsOverview
        pageLabel="All Goals"
        summary={summary}
        carryoverCount={carryoverCount}
        lifecycle={lifecycle}
        statusReviewCount={statusReviewCount}
        reviewSuggestedOnly={reviewSuggestedOnly}
        onLifecycleChange={(value) => {
          setLifecycle(value);
          setReviewSuggestedOnly(false);
        }}
        onReview={() => {
          setFilters(emptyMyGoalsFilters);
          setSelectedOwners([]);
          setSelectedGroups([]);
          setSelectedTeams([]);
          setLifecycle('all');
          setReviewSuggestedOnly(true);
        }}
      />

      <TeamGoalsToolbar
        pageLabel="All Goals"
        goals={displayGoals}
        filters={filters}
        sort={sort}
        groupBy={groupBy}
        groupByOptions={[
          {value: 'group', label: groupTypeLabel},
          {value: 'team', label: 'Teams'},
          {value: 'none', label: 'None'},
        ]}
        period={dateRange}
        defaultPeriod={defaultPeriod}
        memberOptions={ownerOptions}
        selectedPeople={selectedOwners}
        reviewSuggestedOnly={reviewSuggestedOnly}
        onClearStatusReview={() => setReviewSuggestedOnly(false)}
        peopleLabel="Owners"
        selectionFilterGroups={[
          ...(groupOptions.length
            ? [
                {
                  key: 'groups',
                  title: groupTypeLabel,
                  options: groupOptions,
                  selected: selectedGroups,
                  onChange: setSelectedGroups,
                },
              ]
            : []),
          ...(teamOptions.length
            ? [
                {
                  key: 'teams',
                  title: 'Teams',
                  options: teamOptions,
                  selected: selectedTeams,
                  onChange: setSelectedTeams,
                },
              ]
            : []),
        ]}
        searchValue={searchInput}
        searchPlaceholder="Search goals"
        filterSearchable
        resultCount={visibleGoals.length}
        onSearchChange={(value) => {
          setSearchInput(value);
          debouncedSearch(value);
        }}
        onFiltersChange={(value) => {
          setFilters(value);
          setReviewSuggestedOnly(false);
        }}
        onSortChange={setSort}
        onGroupByChange={setGroupBy}
        onPeriodChange={setDateRange}
        onSelectedPeopleChange={(value) => {
          setSelectedOwners(value);
          setReviewSuggestedOnly(false);
        }}
      />

      {visibleGoals.length === 0 ? (
        <EmptyPanel>
          <div>
            <h2>No goals match these filters</h2>
            <p>
              {hasActiveFilters
                ? 'Try clearing a filter, changing the period, or using another search.'
                : 'Workspace goals will appear here when they are created.'}
            </p>
          </div>
        </EmptyPanel>
      ) : groupBy !== 'none' ? (
        <GoalSections>
          {sections.map((section) => {
            const expanded = expandedSections.has(section.id);
            return (
              <GoalSection key={section.id}>
                <GoalSectionHeader
                  type="button"
                  aria-expanded={expanded}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} ${
                    section.name
                  } goals`}
                  onClick={() =>
                    setExpandedSections((current) => {
                      const next = new Set(current);
                      next.has(section.id)
                        ? next.delete(section.id)
                        : next.add(section.id);
                      return next;
                    })
                  }>
                  <GoalSectionAvatar aria-hidden="true">
                    {sectionInitials(section.name)}
                  </GoalSectionAvatar>
                  <GoalSectionIdentity>
                    <strong>{section.name}</strong>
                    <small>
                      {section.ownerCount} owner
                      {section.ownerCount === 1 ? '' : 's'} ·{' '}
                      {section.goals.length} goal
                      {section.goals.length === 1 ? '' : 's'}
                    </small>
                  </GoalSectionIdentity>
                  <GoalSectionChevron $expanded={expanded} aria-hidden="true">
                    <FiChevronDown size={14} />
                  </GoalSectionChevron>
                </GoalSectionHeader>
                {expanded && (
                  <GoalSectionCards>
                    {section.goals.map(renderCard)}
                  </GoalSectionCards>
                )}
              </GoalSection>
            );
          })}
        </GoalSections>
      ) : (
        <>
          <FlatGoalCards>{pageGoals.map(renderCard)}</FlatGoalCards>
          {visibleGoals.length > PAGE_SIZE && (
            <PaginationBar>
              <span>
                Page {page} of {totalPages}
              </span>
              <div>
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((value) => value - 1)}>
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((value) => value + 1)}>
                  Next
                </button>
              </div>
            </PaginationBar>
          )}
        </>
      )}
    </AllGoalsContent>
  );
});
